/** Card 3 — Ask Lane demo. Three scripted questions with a typed reveal. */
import React, { useEffect, useState } from "react";
import { TransitionPanel, DemoBadge, OptionButton, trackStep, trackCompleted } from "../activationShared";

const DEMOS = [
    {
        q: "My dad thinks I'm stealing from him.",
        a: "That accusation cuts deep — especially when you're the one showing up every day. Please hear this first: it isn't him, and it isn't about you. Paranoia about money and theft is one of the most common symptoms in the middle stages of dementia. His brain is trying to explain why things feel missing or confusing, and it reaches for the nearest story.\n\nWhat usually helps: don't argue the facts — you can't win a logic debate with dementia. Instead, respond to the feeling underneath: \"That would be scary. Let's look together.\" Keep a small 'finding place' for the things he hides, and let his doctor know — sometimes paranoia has treatable causes.\n\nAnd for you: say it out loud to someone safe. Being accused by the person you're caring for is one of the loneliest parts of this road. You're doing nothing wrong.",
    },
    {
        q: "How do I get her to shower without a fight?",
        a: "Bathing battles are rarely about being clean — they're usually about fear: cold air, slippery floors, loss of privacy, water hitting the head. When you know what she's protecting herself from, the fight makes more sense.\n\nA few things that work for many families: warm the bathroom first and have towels ready. Say \"it's time for your shower\" rather than asking (questions invite \"no\"). Try a hand-held sprayer and a shower chair so water never startles her. If mornings fail, try after lunch — resistance often follows a daily rhythm.\n\nAnd lower the bar kindly: a full shower twice a week plus sponge baths in between keeps her healthy. You don't have to win every day.",
    },
    {
        q: "Is it time for him to stop driving?",
        a: "This is one of the hardest calls families make, because driving is independence — and grief comes with taking it away.\n\nWarning signs that mean it's time to act: getting lost on familiar routes, new dents or close calls, confusing pedals, drifting between lanes, or other drivers honking often. If any of those are happening, the risk isn't just his.\n\nYou don't have to be the villain: ask his doctor to write a \"no driving\" order (many will — let the white coat take the blame), request a driving evaluation through an occupational therapist, and in many states you can file a confidential report with the DMV. Then fill the gap fast — rides from family, GoGoGrandparent, or community senior transport — because \"how will I get anywhere?\" is the real fear underneath.",
    },
];

export default function AskLaneDemo({ onClose }) {
    const [picked, setPicked] = useState(null);
    const [shown, setShown] = useState(0);
    const paras = picked !== null ? DEMOS[picked].a.split("\n\n") : [];

    useEffect(() => {
        if (picked === null) return;
        setShown(0);
        let i = 0;
        const t = setInterval(() => {
            i += 1;
            setShown(i);
            if (i >= DEMOS[picked].a.split("\n\n").length) {
                clearInterval(t);
                trackCompleted("ask_lane");
            }
        }, 900);
        return () => clearInterval(t);
    }, [picked]);

    return (
        <div data-testid="activation-ask-lane">
            <DemoBadge>Sample conversation — anonymous, nothing is saved</DemoBadge>
            <h3 className="mt-4 font-heading font-medium text-lg md:text-xl text-[#1F2624]">
                {picked === null ? "What would you ask? Pick a real question other caregivers have asked Lane." : "You asked:"}
            </h3>
            {picked === null ? (
                <div className="mt-4 space-y-2">
                    {DEMOS.map((d, i) => (
                        <OptionButton key={d.q} onClick={() => { setPicked(i); trackStep("ask_lane", `question_${i + 1}`); }} testid={`asklane-q${i + 1}`}>
                            &ldquo;{d.q}&rdquo;
                        </OptionButton>
                    ))}
                </div>
            ) : (
                <div className="mt-3">
                    <div className="rounded-2xl rounded-br-md bg-teal-dark text-white px-4 py-3 text-sm ml-auto max-w-[85%] w-fit">
                        {DEMOS[picked].q}
                    </div>
                    <div className="mt-3 space-y-3" data-testid="asklane-response">
                        {paras.slice(0, shown).map((p, i) => (
                            <div key={i} className="rounded-2xl rounded-bl-md bg-cream-light border border-[#EDE6D6] px-4 py-3 text-sm text-[#2A3330] leading-relaxed max-w-[92%] animate-fade-in-up">
                                {p}
                            </div>
                        ))}
                        {shown < paras.length && (
                            <div className="rounded-2xl rounded-bl-md bg-cream-light border border-[#EDE6D6] px-4 py-3 w-fit">
                                <span className="inline-flex gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#9CA6A1] animate-bounce" />
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#9CA6A1] animate-bounce [animation-delay:120ms]" />
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#9CA6A1] animate-bounce [animation-delay:240ms]" />
                                </span>
                            </div>
                        )}
                    </div>
                    {shown >= paras.length && (
                        <>
                            <p className="mt-4 text-xs text-[#6E7A74] italic" data-testid="asklane-sample-note">
                                This was a sample conversation. Lane&apos;s real answers are shaped around your loved one — their stage, history, and what you&apos;ve already tried.
                            </p>
                            <button type="button" onClick={() => { setPicked(null); setShown(0); }} data-testid="asklane-another"
                                className="mt-3 text-sm font-medium text-teal-dark hover:underline">
                                Try another question
                            </button>
                            <TransitionPanel card="ask_lane" onClose={onClose} ctaLabel="Create your account for guidance that remembers your family's story" />
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
